import { errorSchema } from "@app/shared";
import {
  type QueryClient,
  type UseMutationResult,
  type UseQueryResult,
  useMutation,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";
import { z } from "zod";
import { ApiError, apiGet } from "../lib/api";
import { apiPost, apiPut } from "./api-ecrire";
import {
  type ApercuManuscrit,
  apercuManuscritSchema,
  type briefEcritSchema,
  type ConflitTexte,
  conflitTexteSchema,
  type creerLivreSchema,
  type DoubleDetail,
  doubleDetailSchema,
  type ListeLivres,
  type LivreDetail,
  listeLivresSchema,
  livreCreeSchema,
  livreDetailSchema,
  type modifierDoubleSchema,
  type PersonnageEcrit,
  type TexteEcrit,
  texteEcritSchema,
} from "./contract";

/**
 * Les lectures et les ecritures du module Histoires (#226), sur le modele du coloriage
 * (`coloriage-requetes.ts`), sans rien lui importer.
 *
 * Les lectures ne reessayent pas : un 401, un 403 ou un 404 est une reponse, pas une panne.
 * Toutes les cles commencent par `histoires`, une ecriture invalide ce qu'elle a change.
 *
 * Le texte d'une double a son propre chemin (`ecrireTexte`, `effacerTexte`, `restaurerLigne`) :
 * l'ecran tient lui-meme sa sauvegarde automatique (`histoires-sauvegarde.ts`), doit lire le
 * corps d'un 409 pour dire qui a ecrit, distinguer le reseau coupe du Lab a l'arret, et envoyer
 * la derniere saisie quand la page se ferme (`keepalive`). Aucune de ces ecritures ne leve.
 */

const commun = { retry: false, refetchOnWindowFocus: false } as const;

export const CLE_HISTOIRES = ["histoires"] as const;

export function cleLivres(): readonly unknown[] {
  return [...CLE_HISTOIRES, "livres"];
}

export function cleLivre(id: string): readonly unknown[] {
  return [...CLE_HISTOIRES, "livre", id];
}

export function cleDouble(id: string): readonly unknown[] {
  return [...CLE_HISTOIRES, "double", id];
}

/** `GET /api/histoires/livres` : l'accueil. */
export function useLivres(): UseQueryResult<ListeLivres, Error> {
  return useQuery({
    queryKey: cleLivres(),
    queryFn: () => apiGet("/api/histoires/livres", listeLivresSchema),
    ...commun,
  });
}

/** `GET /api/histoires/livres/{id}` : le livre, son chemin de fer, son brief, ses personnages. */
export function useLivre(id: string, actif = true): UseQueryResult<LivreDetail, Error> {
  return useQuery({
    queryKey: cleLivre(id),
    queryFn: () => apiGet(`/api/histoires/livres/${encodeURIComponent(id)}`, livreDetailSchema),
    enabled: actif,
    ...commun,
  });
}

/** `GET /api/histoires/doubles/{id}` : la double, son journal, ses voisines. */
export function useDouble(id: string, actif = true): UseQueryResult<DoubleDetail, Error> {
  return useQuery({
    queryKey: cleDouble(id),
    queryFn: () => apiGet(`/api/histoires/doubles/${encodeURIComponent(id)}`, doubleDetailSchema),
    enabled: actif,
    ...commun,
  });
}

/** Le message a afficher sous un bouton quand une ecriture est refusee. */
export function messageDEchec(error: unknown): string {
  return error instanceof ApiError ? error.message : "L'écriture n'a pas abouti.";
}

/**
 * Les reponses que le contrat ne decrit pas (le brief, une double, un personnage) : l'ecran
 * n'en lit rien et relit ce qu'il affiche. Un 2xx sans corps passe aussi.
 */
const reponseIgnoree = z.unknown();

export type CreerLivre = z.infer<typeof creerLivreSchema>;
export type LivreCree = z.infer<typeof livreCreeSchema>;

/** `POST /api/histoires/livres` : un livre neuf, au plan par defaut. */
export function useCreerLivre(): UseMutationResult<LivreCree, Error, CreerLivre> {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (livre: CreerLivre) => apiPost("/api/histoires/livres", livre, livreCreeSchema),
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: cleLivres() });
    },
  });
}

export type BriefEcrit = z.infer<typeof briefEcritSchema>;

/** `PUT /api/histoires/livres/{id}/brief` : le brief et la phrase de style. */
export function useEcrireBrief(livreId: number): UseMutationResult<unknown, Error, BriefEcrit> {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (brief: BriefEcrit) =>
      apiPut(`/api/histoires/livres/${livreId}/brief`, brief, reponseIgnoree),
    onSuccess: () => {
      // Les doubles lisent le brief aussi (mots interdits, fourchette) : tout le module se relit.
      void client.invalidateQueries({ queryKey: CLE_HISTOIRES });
    },
  });
}

export type Manuscrit = { readonly texte: string; readonly essai: boolean };

/**
 * `POST /api/histoires/livres/{id}/manuscrit` : l'apercu (`essai: true`) n'ecrit rien ; le
 * remplissage invalide tout le module, dont l'accueil qui compte les doubles ecrites.
 */
export function useManuscrit(
  livreId: number,
): UseMutationResult<ApercuManuscrit, Error, Manuscrit> {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (manuscrit: Manuscrit) =>
      apiPost(`/api/histoires/livres/${livreId}/manuscrit`, manuscrit, apercuManuscritSchema),
    onSuccess: (reponse) => {
      if (reponse.applique) void client.invalidateQueries({ queryKey: CLE_HISTOIRES });
    },
  });
}

export type PersonnageAEcrire = {
  /** Absent pour un personnage neuf. */
  readonly id?: number | undefined;
  readonly personnage: PersonnageEcrit;
};

/**
 * `POST /api/histoires/livres/{id}/personnages` (neuf) et `PUT /api/histoires/personnages/{id}`.
 * Les doubles montrent les personnages du livre : tout le module se relit.
 */
export function useEcrirePersonnage(
  livreId: number,
): UseMutationResult<unknown, Error, PersonnageAEcrire> {
  const client = useQueryClient();
  return useMutation({
    mutationFn: ({ id, personnage }: PersonnageAEcrire) =>
      id === undefined
        ? apiPost(`/api/histoires/livres/${livreId}/personnages`, personnage, reponseIgnoree)
        : apiPut(`/api/histoires/personnages/${id}`, personnage, reponseIgnoree),
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: CLE_HISTOIRES });
    },
  });
}

export type ModifierDouble = z.infer<typeof modifierDoubleSchema>;

/**
 * `PUT /api/histoires/doubles/{id}` : les metadonnees d'une double (intention, gabarit,
 * composition, personnages, figurants), un champ absent ne change pas. Le texte ne passe jamais
 * par la : sa version ne bouge pas, et l'editeur ne se reinitialise pas a la relecture.
 */
export function useModifierDouble(
  doubleId: number,
): UseMutationResult<unknown, Error, ModifierDouble> {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (modification: ModifierDouble) =>
      apiPut(`/api/histoires/doubles/${doubleId}`, modification, reponseIgnoree),
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: cleDouble(String(doubleId)) });
      void client.invalidateQueries({ queryKey: [...CLE_HISTOIRES, "livre"] });
    },
  });
}

// ---------------------------------------------------------------------------------------
// Le texte d'une double.

export type CourantTexte = ConflitTexte["courant"];

/** Ce que rend une ecriture de texte. Aucune ne leve : l'ecran decide de la suite. */
export type ResultatEcriture =
  | { readonly type: "ok"; readonly reponse: TexteEcrit }
  /** 409 : le texte a change ailleurs, et voici le texte courant. */
  | { readonly type: "conflit"; readonly courant: CourantTexte }
  /** La requete n'est pas partie : le reseau est coupe. */
  | { readonly type: "hors_ligne" }
  /** 503 : le Lab est a l'arret (`stop_all`) ou la console en pause. */
  | { readonly type: "arret" }
  /** 401 ou 403 : la session a pris fin, ou l'adresse n'est plus sur la liste. */
  | { readonly type: "session"; readonly statut: number }
  /**
   * Tout autre refus, avec le message de la console et son code quand le corps en a un
   * (`double_vide` : « Repartir de zéro » sur une double deja vide, que l'ecran relit).
   */
  | {
      readonly type: "refus";
      readonly statut: number;
      readonly message: string;
      readonly code?: string | undefined;
    };

/**
 * Une ecriture qui ne repond pas en 30 s (le delai du frontal) est abandonnee et comptee hors
 * ligne : sans cela, un telephone qui perd le reseau en pleine requete attendrait sans fin, et
 * aucune saisie ne repartirait. Si la console l'avait tout de meme enregistree, le nouvel essai
 * fera un 409 : rien ne s'ecrase, rien ne se perd.
 */
export const DELAI_ECRITURE_MS = 30_000;

async function ecrireUnTexte(
  methode: "PUT" | "POST",
  chemin: string,
  corps: unknown,
  keepalive: boolean,
): Promise<ResultatEcriture> {
  const abandon = new AbortController();
  const minuterie = setTimeout(() => abandon.abort(), DELAI_ECRITURE_MS);
  let response: Response;
  let payload: unknown;
  try {
    response = await fetch(chemin, {
      method: methode,
      headers: { accept: "application/json", "content-type": "application/json" },
      body: JSON.stringify(corps),
      signal: abandon.signal,
      // La derniere saisie part encore quand la page se ferme (au-dela de 64 Ko, le navigateur
      // refuse : la saisie reste alors dans le brouillon local).
      ...(keepalive ? { keepalive: true } : {}),
    });
    payload = await response.json().catch(() => null);
  } catch {
    return { type: "hors_ligne" };
  } finally {
    clearTimeout(minuterie);
  }
  if (response.ok) {
    const lu = texteEcritSchema.safeParse(payload);
    return lu.success
      ? { type: "ok", reponse: lu.data }
      : {
          type: "refus",
          statut: response.status,
          message: "Réponse hors contrat : recharge la double pour voir ce qui est enregistré.",
        };
  }
  if (response.status === 409) {
    const conflit = conflitTexteSchema.safeParse(payload);
    if (conflit.success) return { type: "conflit", courant: conflit.data.courant };
  }
  if (response.status === 503) return { type: "arret" };
  if (response.status === 401 || response.status === 403) {
    return { type: "session", statut: response.status };
  }
  const erreur = errorSchema.safeParse(payload);
  if (!erreur.success) {
    return { type: "refus", statut: response.status, message: `Erreur HTTP ${response.status}` };
  }
  return {
    type: "refus",
    statut: response.status,
    code: erreur.data.error.code,
    message: erreur.data.error.message,
  };
}

/** `PUT /api/histoires/doubles/{id}/texte` : le texte, avec la version qu'il remplace. */
export function ecrireTexte(
  doubleId: number,
  corps: { readonly texte: string; readonly version: number },
  { keepalive = false }: { readonly keepalive?: boolean } = {},
): Promise<ResultatEcriture> {
  return ecrireUnTexte("PUT", `/api/histoires/doubles/${doubleId}/texte`, corps, keepalive);
}

/** `POST /api/histoires/doubles/{id}/effacer` : « Repartir de zéro ». */
export function effacerTexte(doubleId: number, version: number): Promise<ResultatEcriture> {
  return ecrireUnTexte("POST", `/api/histoires/doubles/${doubleId}/effacer`, { version }, false);
}

/**
 * `POST /api/histoires/doubles/{id}/journal/{ligne}/restaurer` : le texte et la provenance d'une
 * ligne du journal, restauree sous sa double (le serveur verifie qu'elle en est bien une).
 */
export function restaurerLigne(
  doubleId: number,
  ligneId: number,
  version: number,
): Promise<ResultatEcriture> {
  return ecrireUnTexte(
    "POST",
    `/api/histoires/doubles/${doubleId}/journal/${ligneId}/restaurer`,
    { version },
    false,
  );
}

/**
 * Pose dans la double en cache ce que la console a maintenant, pour qu'un retour sur la double
 * reparte de la bonne version (sans cela, le cache rendrait l'ancienne et la saisie suivante
 * ferait un faux 409). Une relecture partie avant l'ecriture (le retour sur la page) rendrait
 * l'ancienne version par-dessus : elle cede la place a une nouvelle, qui lit la console apres
 * l'ecriture. Jamais par-dessus une version plus recente : une relecture arrivee pendant
 * l'ecriture (Claude a ecrit apres nous) reste en cache. Le chemin de fer et l'accueil sont
 * marques perimes, sans relecture : ils se relisent a la prochaine visite.
 */
function poserDansLaDouble(
  client: QueryClient,
  doubleId: number,
  texte: Pick<DoubleDetail["double"], "texte" | "texte_version" | "provenance"> &
    Partial<Pick<DoubleDetail["double"], "maj_le">>,
): void {
  const cle = cleDouble(String(doubleId));
  const relectureEnVol = client.isFetching({ queryKey: cle }) > 0;
  client.setQueryData<DoubleDetail>(cle, (detail) =>
    detail === undefined || detail.double.texte_version > texte.texte_version
      ? detail
      : { ...detail, double: { ...detail.double, ...texte } },
  );
  // La relecture en vol est abandonnee sans rien poser (`cancelRefetch`), et une autre part,
  // meme pour une double qu'on vient de quitter.
  if (relectureEnVol) void client.invalidateQueries({ queryKey: cle, refetchType: "all" });
  void client.invalidateQueries({ queryKey: cleLivres(), refetchType: "none" });
  void client.invalidateQueries({ queryKey: [...CLE_HISTOIRES, "livre"], refetchType: "none" });
}

/** Pose un texte que la console vient d'enregistrer dans la double en cache. */
export function poserLeTexte(
  client: QueryClient,
  doubleId: number,
  texte: string,
  reponse: TexteEcrit,
): void {
  poserDansLaDouble(client, doubleId, {
    texte,
    texte_version: reponse.texte_version,
    provenance: reponse.provenance,
    maj_le: reponse.maj_le,
  });
}

/**
 * Pose dans la double en cache le texte courant d'un 409 reconnu comme notre propre ecriture (le
 * rattrapage) : son texte, sa version et sa provenance. Le 409 ne dit pas l'heure : la double
 * garde la sienne jusqu'a la prochaine relecture.
 */
export function poserLeCourant(client: QueryClient, doubleId: number, courant: CourantTexte): void {
  poserDansLaDouble(client, doubleId, {
    texte: courant.texte,
    texte_version: courant.texte_version,
    provenance: courant.provenance,
  });
}

/** Relit la double (son journal surtout), et marque le reste du livre perime. */
export function relireLaDouble(client: QueryClient, doubleId: number): void {
  void client.invalidateQueries({ queryKey: cleDouble(String(doubleId)) });
  void client.invalidateQueries({ queryKey: cleLivres(), refetchType: "none" });
  void client.invalidateQueries({ queryKey: [...CLE_HISTOIRES, "livre"], refetchType: "none" });
}
